import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "react-query";
import { useParams } from "react-router-dom";
import MaterialReactTable from "material-react-table";
import {
  Spinner,
  Toast,
  ToastBody,
  ToastHeader,
  Modal,
  ModalHeader,
  ModalBody,
  Table,
} from "reactstrap";
import useAxios from "../../sections/useAxios";
import { toast } from "react-toastify";
import { formatDateDDMMYYYY } from "../../utils/formatDate";
import ConfirmDialog from "../../components/common/confirmation-dialog.jsx";

const InvestorMeetingsListing = () => {
  const { id } = useParams();
  const api = useAxios();

  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const {
    data,
    isLoading: meetingsLoading,
    isError,
    error,
    refetch,
  } = useQuery(
    ["all-investor-meetings", id],
    async () => {
      const res = await api.get(`company/all-meeting-investors/${id}`);
      return res.data;
    },
    { cacheTime: 0, enabled: !!id }
  );

  const company = data?.company ?? "";
  const meetings = data?.investors ?? [];

  const deleteMutation = useMutation(
    async (regId) => {
      return api.delete(`company/admin-cancel-registration/${regId}`);
    },
    {
      onSuccess: () => {
        toast.success("Registration cancelled successfully");
        setConfirmDialogOpen(false);
        refetch();
        if (selectedInvestor) {
          setSelectedInvestor((prev) => ({
            ...prev,
            registrations: prev.registrations.filter(
              (r) => r.id !== deleteId
            ),
          }));
        }
      },
      onError: () => {
        toast.error("Error cancelling registration");
        setConfirmDialogOpen(false);
      },
    }
  );

  useEffect(() => {
    if (isError) {
      setToastMsg(error.message || "Something went wrong");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 4000);
    }
  }, [isError, error]);

  const columns = [
    { accessorKey: "id", header: "ID", size: 50 },
    { accessorKey: "name", header: "Investor Name" },
    { accessorKey: "email", header: "Email" },
    {
      accessorKey: "registration_count",
      header: "Registration Count",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "registrations",
      header: "Registrations",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => (
        <i
          className="fa-solid fa-clipboard-list fa-xl text-primary"
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedInvestor(row.original);
            setModalOpen(true);
          }}
        ></i>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      Cell: ({ cell }) => new Date(cell.getValue()).toLocaleDateString(),
    },
  ];

  return (
    <div className="container my-4">
      <h4 className="primary-color fw-bold">Meetings for Company (ID: {id})</h4>
      <div className="d-flex align-items-center mb-3">
        <i
          className="fa-solid fa-building me-2 text-center primary-color"
          style={{ width: "20px" }}
        ></i>{" "}
        <h6 className="mb-0">{company}</h6>
      </div>

      {meetingsLoading && (
        <div className="d-flex justify-content-center align-items-center my-4">
          <Spinner color="primary" />
        </div>
      )}

      {showToast && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 1050,
            minWidth: "250px",
          }}
        >
          <Toast isOpen={showToast}>
            <ToastHeader icon="danger">Error</ToastHeader>
            <ToastBody>{toastMsg}</ToastBody>
          </Toast>
        </div>
      )}

      {!meetingsLoading && !isError && (
        <MaterialReactTable
          columns={columns}
          data={meetings}
          muiTablePaperProps={{
            elevation: 10,
          }}
        />
      )}

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="lg">
        <ModalHeader
          className="primary-color fw-bold"
          toggle={() => setModalOpen(!modalOpen)}
        >
          Registrations for {selectedInvestor?.name}
        </ModalHeader>
        <ModalBody>
          {selectedInvestor?.registrations?.length > 0 ? (
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Meeting</th>
                  <th>Company</th>
                  <th>Location</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedInvestor.registrations.map((reg) => (
                  <tr key={reg.id}>
                    <td>{reg.id}</td>
                    <td>{reg.meeting}</td>
                    <td>{reg.company}</td>
                    <td>{reg.location}</td>
                    <td>{reg.start_time}</td>
                    <td>{reg.end_time}</td>
                    <td className="text-center">
                      <i
                        className="fa-solid fa-trash text-danger pointer"
                        onClick={() => {
                          setDeleteId(reg.id);
                          setConfirmDialogOpen(true);
                        }}
                      ></i>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p>No registrations found for this investor.</p>
          )}
        </ModalBody>
      </Modal>

      <ConfirmDialog
        isOpen={confirmDialogOpen}
        toggle={() => setConfirmDialogOpen(!confirmDialogOpen)}
        title="Confirm Delete"
        message="Are you sure you want to cancel this registration?"
        onConfirm={() => deleteMutation.mutate(deleteId)}
        loading={deleteMutation.isLoading}
      />
    </div>
  );
};

export default InvestorMeetingsListing;
